import { v4 as uuidv4 } from 'uuid';

import {
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import {
    isFinalizedPhoto,
    isInMemoryPhoto,
    isUploadedPhoto,
    isUploadPendingPhoto,
    photoAmountLimit,
    photoImageFormat,
    photoMaxPixelSize,
    photoSizeLimit,
    photoSizeLimitInBytes
} from '@shared/*';
import { fadeInOutAnimation } from '@animations/*';
import { FinalizedPhoto, InMemoryPhoto, PhotoType, UploadedPhoto, UploadPendingPhoto } from '@models/*';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ZenithComponentsModule } from '@mateco/zenith-webcomponents-angular/dist/zenith-webcomponents-angular';
import { ZenithToastService } from '../zenith/zenith-toast/zenith-toast.service';
import { Icon, ZenithAppearance } from '../zenith/zenith-enums';

@Component({
    standalone: true,
    selector: 'app-photo-upload',
    templateUrl: './photo-upload.component.html',
    styleUrls: ['./photo-upload.component.scss'],
    animations: [fadeInOutAnimation],
    imports: [TranslateModule, CommonModule, ZenithComponentsModule],
})
export class PhotoUploadComponent {
    photosAllowed = input<boolean>();
    // Parents change this array by reference instead of passing a new one, so the input signal never sees a change and a computed() based on it won't update. Read photos() directly instead.
    photos = input<Array<InMemoryPhoto | UploadedPhoto | UploadPendingPhoto | FinalizedPhoto>>([]);
    photoType = input<PhotoType>();
    formDisabled = input<boolean>();
    readonly = input<boolean>(false);
    addPhoto = output<InMemoryPhoto>();
    removePhoto = output<{ index: number }>();

    photoUpload = viewChild.required<ElementRef<HTMLInputElement>>('photoUpload');


    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly zenithToastService = inject(ZenithToastService);

    // TODO: replace this flag by `destroyRef.destroyed` once we upgrade to Angular 19+.
    private destroyed = false;

    constructor() {
        inject(DestroyRef).onDestroy(() => this.destroyed = true);
    }

    limit = computed(() => ({
        amount: photoAmountLimit[this.photoType()] || 1,
        size: photoSizeLimit,
    }));
    isProcessing = signal(false);
    captureMode = signal<string>(null);

    // A getter rather than a signal, see photos input
    get isPhotoLimitReached() {
        return this.photos().length >= this.limit().amount;
    }

    capturePhoto(captureMode: string) {
        this.captureMode.set(captureMode);
        this.changeDetectorRef.detectChanges();
        this.photoUpload().nativeElement.click()
    }

    get buttonTranslationKey() {
        switch (this.photoType()) {
            case PhotoType.CHECKUP:
                return 'CHECKLIST.ADD_CHECKUP_PHOTO';
            case PhotoType.DAMAGE:
                return 'CHECKLIST.ADD_DAMAGE_PHOTO';
            case PhotoType.ISSUE_REPORT:
            default:
                return 'CHECKLIST.ADD_PHOTO';
        }
    }

    public isUploadedPhoto = isUploadedPhoto;
    public isInMemoryPhoto = isInMemoryPhoto;
    public isUploadPendingPhoto = isUploadPendingPhoto;
    public isFinalizedPhoto = isFinalizedPhoto;

    private async handleSelection(file: File) {
        // Decodes the file without first copying it into a base64 data url
        const image = await createImageBitmap(file);
        const canvas = document.createElement('canvas') as HTMLCanvasElement;
        try {
            const context = canvas.getContext('2d');
            const { width: resizedWidth, height: resizedHeight } = this.scaleToFitMaxSize(image);
            context.imageSmoothingQuality = 'high';
            context.imageSmoothingEnabled = true;
            canvas.width = resizedWidth;
            canvas.height = resizedHeight;
            context.drawImage(image, 0, 0, resizedWidth, resizedHeight);
            const resizedImage = canvas.toDataURL(photoImageFormat);
            // Must use data url first, because bytes cannot be rendered in the UI (to show uploaded images)
            // We will convert the base64 data to bytes upon protocol submission (checklist.effect)

            this.addPhoto.emit({ id: uuidv4(), data: resizedImage, type: this.photoType() });
        } finally {
            // Free the decoded pixels and the canvas backing store right away instead of waiting for GC
            image.close();
            canvas.width = 0;
            canvas.height = 0;
        }
    }

    // Sets the longest side exactly instead of scaling it, so it can't round down to one pixel less
    private scaleToFitMaxSize({ width, height }: ImageBitmap): { width: number, height: number } {
        if (Math.max(width, height) <= photoMaxPixelSize) {
            return { width, height };
        }
        return width > height
            ? { width: photoMaxPixelSize, height: height * (photoMaxPixelSize / width) }
            : { width: width * (photoMaxPixelSize / height), height: photoMaxPixelSize };
    }

    public async handleInput(event: Event): Promise<void> {
        const selectedFiles = this.takeSelectedFiles(event.target as HTMLInputElement);
        if (this.isPhotoLimitReached || !selectedFiles.length) {
            return;
        }
        await this.addPhotos(this.withoutTooLargeFiles(selectedFiles));
    }

    private takeSelectedFiles(input: HTMLInputElement): File[] {
        const selectedFiles = Array.from(input.files ?? []);
        input.value = '';
        return selectedFiles;
    }

    private withoutTooLargeFiles(files: File[]): File[] {
        const smallEnoughFiles = files.filter(file => file.size <= photoSizeLimitInBytes);
        if (smallEnoughFiles.length < files.length) { // A too large photo exists
            this.showError('CHECKLIST.PHOTO_SIZE_LIMIT_ERROR', this.limit());
        }
        return smallEnoughFiles;
    }

    private async addPhotos(files: File[]): Promise<void> {
        // Counted up front: the parent adds each emitted photo to `photos`, which changes its length
        const freeSlots = this.limit().amount - this.photos().length;
        let addedPhotos = 0;
        let failedPhotos = 0;
        this.isProcessing.set(true);
        try {
            // One at a time, so the decoded photos don't all take up memory at once
            for (const file of files) {
                // Outputs throw once the component is destroyed, e.g. when the user leaves the page meanwhile
                if (addedPhotos === freeSlots || this.destroyed) {
                    break;
                }
                if (await this.tryAddPhoto(file)) {
                    addedPhotos++;
                } else {
                    failedPhotos++;
                }
            }
        } finally {
            this.isProcessing.set(false);
        }
        if (failedPhotos && !this.destroyed) {
            this.showError('CHECKLIST.PHOTOS_PROCESSING_ERROR', { failed: failedPhotos, total: addedPhotos + failedPhotos });
        }
    }

    private async tryAddPhoto(file: File): Promise<boolean> {
        try {
            await this.handleSelection(file);
            return true;
        } catch {
            return false;
        }
    }

    private showError(translationKey: string, params: object): void {
        this.zenithToastService.showToast(
            this.translateService.instant(translationKey, params),
            ZenithAppearance.ERROR,
            Icon.ERROR
        );
    }

    public handleRemovePhoto(index: number): void {
        this.removePhoto.emit({ index });
    }
}
